import { io, type Socket } from "socket.io-client";
import "@fontsource/bowlby-one-sc";
import "@fontsource/space-mono/400.css";
import "@fontsource-variable/exo-2";
import type {
  ClientToServerEvents,
  EventAck,
  GameSettings,
  RoomState,
  ServerToClientEvents,
} from "../shared/protocol";
import { AudioDirector } from "./audio";
import { bootstrapDiscord, inviteFriends, type DiscordBootstrap } from "./discord";
import type { GameSfxEvent } from "./game/types";
import { AppView } from "./ui/AppView";
import { InputController } from "./ui/InputController";
import type { ActivityViewModel, InputSnapshot } from "./ui/types";
import "./styles.css";

type ActivitySocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type GameWorldLike = {
  applyRoomState?(state: RoomState): void;
  dispose?(): void;
  resize?(): void;
  setInput?(input: InputSnapshot): void;
  setInputSink?(sink: (input: InputSnapshot) => void): void;
  setLocalPlayer?(id: string): void;
  setQuality?(quality: "auto" | "low" | "medium" | "high"): void;
  setReducedMotion?(reduced: boolean): void;
  start?(): void;
};

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("TideBreak could not find its app mount point.");

const audio = new AudioDirector();
if (new URLSearchParams(window.location.search).get("mute") === "1") {
  // Silent QA/share links are temporary and never overwrite a player's saved mix.
  audio.setMusicVolume(0, false);
  audio.setSfxVolume(0, false);
}
audio.installGestureUnlock(document);

let socket: ActivitySocket | null = null;
let discord: DiscordBootstrap | null = null;
let world: GameWorldLike | null = null;
let input: InputSnapshot = { steer: 0, forward: true, tuck: false, jump: false };
let inputSequence = 0;
let settingsRevision = 0;
let previousPhase: RoomState["phase"] | null = null;
let previousCountdown = 0;

const model: ActivityViewModel = {
  bootstrapDetail: "Finding your Discord party…",
  connection: "connecting",
  error: null,
  identity: null,
  musicRunning: false,
  musicVolume: audio.musicVolume,
  room: null,
};

function render(): void {
  model.musicRunning = audio.isRunning;
  view.render(model);
}

function explainAck<T>(result: EventAck<T> | undefined, fallback: string): boolean {
  if (result?.ok) return true;
  view.toast(result?.error?.message ?? fallback, "error");
  return false;
}

function requireSocket(): ActivitySocket | null {
  if (socket?.connected) return socket;
  view.toast("The party connection is catching up. Try that again in a moment.", "error");
  return null;
}

const view = new AppView(
  root,
  {
    invite: () => {
      void inviteFriends(discord?.sdk ?? null)
        .then((result) => {
          view.toast(result === "copied" ? "Invite link copied." : "Discord invite opened.");
          audio.play("click");
        })
        .catch(() => view.toast("Discord couldn't open the invite. Try the voice-channel invite button.", "error"));
    },
    ready: (ready, skin) => {
      const active = requireSocket();
      if (!active) return;
      active.emit("lobby:ready", { ready, skin }, (result) => {
        if (explainAck(result, "Your ready state did not reach the party.")) {
          audio.play(ready ? "ready" : "click");
        }
      });
    },
    rematch: () => {
      const active = requireSocket();
      if (!active) return;
      active.emit("race:rematch", {}, (result) => {
        if (explainAck(result, "The rematch could not start.")) audio.play("splash");
      });
    },
    retry: () => window.location.reload(),
    setMusicVolume: (value) => {
      audio.setMusicVolume(value);
      model.musicVolume = audio.musicVolume;
      render();
    },
    setting: (key, value) => {
      const active = requireSocket();
      if (!active) return;
      const revision = settingsRevision;
      const patch = { [key]: value } as Pick<GameSettings, typeof key>;
      active.emit("lobby:settings", { revision, patch }, (result) => {
        if (explainAck(result, "That park setting was not saved.")) {
          settingsRevision = result.data?.revision ?? revision + 1;
          audio.play("click");
        } else if (model.room) {
          settingsRevision = model.room.settingsRevision;
        }
      });
    },
    startRace: () => {
      const active = requireSocket();
      if (!active) return;
      active.emit("race:start", {}, (result) => {
        if (!explainAck(result, "The race could not launch.")) return;
        view.announce("Race starting");
      });
    },
  },
  model,
);

const controls = new InputController(root, (next) => {
  const jumpStarted = next.jump && !input.jump;
  input = next;
  world?.setInput?.(next);
  if (jumpStarted) audio.play("jump");
});

function sendRaceInput(next: InputSnapshot = input): void {
  if (!socket?.connected || model.room?.phase !== "racing") return;
  socket.emit("race:input", {
    seq: ++inputSequence,
    steer: Math.min(1, Math.max(-1, next.steer)),
    forward: next.forward,
    tuck: next.tuck,
    jump: next.jump,
  });
}

function handlePhaseAudio(state: RoomState): void {
  if (state.phase === "countdown" && state.countdownEndsAt) {
    const count = Math.max(1, Math.ceil((state.countdownEndsAt - Date.now()) / 1_000));
    if (count !== previousCountdown) {
      previousCountdown = count;
      audio.play("countdown");
      view.announce(String(count));
    }
  } else {
    previousCountdown = 0;
  }

  if (state.phase === previousPhase) return;
  if (state.phase === "racing") {
    audio.play("boost");
    view.announce("Go");
  } else if (state.phase === "results") {
    audio.play("finish");
    controls.reset();
  } else if (state.phase === "lobby" && previousPhase === "results") {
    audio.play("splash");
  }
  previousPhase = state.phase;
}

function handleRoomState(state: RoomState): void {
  model.room = state;
  model.error = null;
  settingsRevision = Math.max(settingsRevision, state.settingsRevision);
  const localId = model.identity?.id;
  if (localId) world?.setLocalPlayer?.(localId);
  world?.applyRoomState?.(state);
  handlePhaseAudio(state);
  render();
}

async function createGameWorld(): Promise<void> {
  const { GameWorld } = await import("./game/GameWorld");
  const Constructor = GameWorld as unknown as new (
    canvas: HTMLCanvasElement,
    options?: Record<string, unknown>,
  ) => GameWorldLike;
  world = new Constructor(view.canvas, {
    localPlayerId: model.identity?.id,
    captureKeyboard: false,
    onSfx: (event: GameSfxEvent) => {
      const mapped = event.type === "land" ? "splash" : event.type === "go" ? "boost" : event.type;
      audio.play(mapped);
    },
  });
  world.setLocalPlayer?.(model.identity?.id ?? "");
  world.setInputSink?.((next) => {
    input = next;
    sendRaceInput(next);
  });
  world.setInput?.(input);
  world.setReducedMotion?.(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  world.setQuality?.("auto");
  world.start?.();
  if (model.room) world.applyRoomState?.(model.room);

  const resize = new ResizeObserver(() => world?.resize?.());
  resize.observe(view.canvas);
  window.addEventListener("pagehide", () => resize.disconnect(), { once: true });
}

function connectMultiplayer(session: DiscordBootstrap): void {
  const { identity } = session;
  if (!identity.ticket) {
    throw new Error("The park server did not issue a multiplayer ticket.");
  }

  socket = io({
    auth: { ticket: identity.ticket, instanceId: identity.instanceId },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 4_000,
    timeout: 12_000,
    transports: ["websocket", "polling"],
  }) as ActivitySocket;

  socket.on("connect", () => {
    model.connection = "connected";
    model.error = null;
    render();
  });
  socket.io.on("reconnect_attempt", () => {
    model.connection = "reconnecting";
    render();
  });
  socket.on("disconnect", (reason) => {
    model.connection = reason === "io client disconnect" ? "offline" : "reconnecting";
    render();
  });
  socket.on("connect_error", (error) => {
    model.connection = "offline";
    if (!model.room) model.error = `The multiplayer lane could not connect: ${error.message}`;
    else view.toast("Party connection lost. Rejoining automatically…", "error");
    render();
  });
  socket.on("room:error", (error) => {
    if (!model.room) model.error = error.message;
    else view.toast(error.message, "error");
    render();
  });
  socket.on("room:state", handleRoomState);
}

async function start(): Promise<void> {
  try {
    void createGameWorld().catch((error) => {
      const message = error instanceof Error ? error.message : "Unknown 3D error";
      model.error = `The 3D park could not load: ${message}`;
      render();
    });
    discord = await bootstrapDiscord((_stage, detail) => {
      if (detail) model.bootstrapDetail = detail;
      render();
    });
    model.identity = discord.identity;
    model.bootstrapDetail = "Joining the starting platform…";
    connectMultiplayer(discord);
    render();
  } catch (error) {
    model.error = error instanceof Error ? error.message : "TideBreak could not start.";
    model.connection = "offline";
    render();
  }
}

window.addEventListener("pagehide", () => {
  controls.destroy();
  socket?.disconnect();
  world?.dispose?.();
  audio.destroy();
  view.destroy();
});

void start();
